import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * `<PatientProjectionView />` — patient-projection of substrate
 * audit-chain operations + federated-query aggregations, decrypted in
 * active session and rendered patient-comprehensible client-side per
 * Issue #51 Pattern B.
 *
 * **Pattern B doctrinal posture: rendering happens here.** The
 * substrate-side projection-service delivers ciphertext records (via
 * `IProjectionServiceClient.queryProjectionRecords`) carrying
 * encrypted `PatientProjectionContent` envelopes. The active session
 * decrypts (via `readPatientProjectionRecords`); this view renders
 * the decrypted envelope into narrative via the patient-side
 * `IRenderingTaxonomy`. Cleartext narrative exists in active session
 * memory only; never at rest at SHT.
 *
 * **Composition shape.** This view is composed from:
 *   - `IActiveSession` for session-resident KEK borrow.
 *   - `IProjectionServiceClient` for record query + erasure paths.
 *   - `IEnvelopeCryptoAdapter` for unwrap/decrypt operations.
 *   - `IRenderingTaxonomy` for client-side narrative rendering.
 *   - `kekVersion` for AAD-binding parity with write-side.
 *   - Optional `tenantId` for multi-tenant retrofit (ADR-020).
 *   - Optional activation-pull trigger (`onRefresh`) — when wired,
 *     refreshes records by re-running `runActivationProjectionPull`
 *     against the buffered server-side surfaces.
 *
 * **NOT WIRED INTO `<App />` shell at v0.** The app shell still
 * mounts the v0 stub via `views/index.ts` because the App composition
 * surface doesn't yet flow IActiveSession / IProjectionServiceClient
 * through props. Wiring the App shell is a separate scope item that
 * touches authentication-flow / app-router composition; left for the
 * next PR. Tests + dev sandbox compose the view directly via the
 * `flow` prop pattern matching `AuthenticationView` / `ConsentView` /
 * `ExportView`.
 */
import * as React from 'react';
import { useLocale } from '../i18n/locale-context.js';
import { decodeContent, runActivationProjectionPull, } from '../projection/activation-pull.js';
import { readPatientProjectionRecords, } from '../projection/session-orchestration.js';
import { PatientProjectionDetailStep } from './_patient_projection/detail-step.js';
import { PatientProjectionEmptyStep } from './_patient_projection/empty-step.js';
import { PatientProjectionErrorStep } from './_patient_projection/error-step.js';
import { PatientProjectionListStep, } from './_patient_projection/list-step.js';
import { PatientProjectionLoadingStep } from './_patient_projection/loading-step.js';
export function PatientProjectionView(props) {
    const { t, locale } = useLocale();
    if (props.flow === undefined) {
        return renderUnwiredScaffold(t);
    }
    return _jsx(PatientProjectionWired, { flow: props.flow, t: t, locale: locale });
}
function renderUnwiredScaffold(t) {
    return (_jsxs("section", { "aria-labelledby": "projection-heading", children: [_jsx("h2", { id: "projection-heading", children: t('projection.heading') }), _jsx("p", { children: t('projection.body') }), _jsx("p", { children: _jsx("em", { children: t('projection.unwired_notice') }) })] }));
}
function PatientProjectionWired(props) {
    const { flow, t, locale } = props;
    const [state, setState] = React.useState({ kind: 'idle' });
    const [reloadCounter, setReloadCounter] = React.useState(0);
    // Initial load on mount + on reloadCounter bumps. We do NOT auto-
    // pull-from-buffer here — that's the user's explicit refresh action
    // (the activation-pull pipeline is meant to run at session activation,
    // before the view mounts). On mount we just read+decrypt what's
    // already on the projection store.
    React.useEffect(() => {
        let cancelled = false;
        setState({ kind: 'loading' });
        void (async () => {
            try {
                const { kekId, key } = flow.session.requireKek();
                const result = await readPatientProjectionRecords({
                    tenantId: flow.tenantId,
                    request: {
                        requestId: `req_view_${Date.now()}`,
                        subjectPciHash: flow.session.subject.subjectPciHash,
                        ...(flow.tenantId !== undefined ? { tenantId: flow.tenantId } : {}),
                    },
                    unwrappedKek: key,
                    kekId,
                    kekVersion: flow.kekVersion,
                    adapter: flow.adapter,
                    client: flow.client,
                });
                const decoded = [];
                for (const decrypted of result.records) {
                    try {
                        decoded.push({
                            decrypted,
                            content: decodeContent(decrypted.cleartextContent),
                        });
                    }
                    catch (e) {
                        // Skip records whose content envelope doesn't decode —
                        // they may be from a future schema version.
                        // eslint-disable-next-line no-console
                        console.warn(`skipping record ${decrypted.record.recordId}: content decode failed`, e);
                    }
                }
                if (!cancelled) {
                    setState({ kind: 'loaded', records: decoded });
                }
            }
            catch (e) {
                if (!cancelled) {
                    setState({
                        kind: 'error',
                        message: e instanceof Error ? e.message : String(e),
                    });
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [flow, reloadCounter]);
    const onRefresh = () => {
        setState({ kind: 'pulling' });
        void (async () => {
            try {
                await runActivationProjectionPull({
                    session: flow.session,
                    client: flow.client,
                    adapter: flow.adapter,
                    ...(flow.tenantId !== undefined ? { tenantId: flow.tenantId } : {}),
                    kekVersion: flow.kekVersion,
                });
                setReloadCounter((n) => n + 1);
            }
            catch (e) {
                setState({
                    kind: 'error',
                    message: e instanceof Error ? e.message : String(e),
                });
            }
        })();
    };
    return (_jsxs("section", { "aria-labelledby": "projection-heading", children: [_jsx("h2", { id: "projection-heading", children: t('projection.heading') }), _jsx("p", { children: t('projection.body') }), renderBody(state, flow, locale, t, onRefresh, setState, () => setReloadCounter((n) => n + 1))] }));
}
function renderBody(state, flow, locale, t, onRefresh, setState, onRetry) {
    switch (state.kind) {
        case 'idle':
        case 'loading':
        case 'pulling':
            return _jsx(PatientProjectionLoadingStep, {});
        case 'error':
            return (_jsx(PatientProjectionErrorStep, { message: state.message, onRetry: onRetry }));
        case 'loaded':
            if (state.records.length === 0) {
                return (_jsxs(_Fragment, { children: [_jsx(PatientProjectionEmptyStep, {}), _jsx("button", { type: "button", onClick: onRefresh, children: t('projection.list.refresh_button') })] }));
            }
            return (_jsx(PatientProjectionListStep, { records: state.records, taxonomy: flow.taxonomy, locale: locale, onSelect: (recordId) => {
                    setState({
                        kind: 'detail',
                        records: state.records,
                        selectedRecordId: recordId,
                    });
                }, onRefresh: onRefresh }));
        case 'detail': {
            const item = state.records.find((r) => r.decrypted.record.recordId === state.selectedRecordId);
            if (item === undefined) {
                return (_jsx(PatientProjectionErrorStep, { message: t('projection.detail.record_not_found'), onRetry: () => setState({ kind: 'loaded', records: state.records }) }));
            }
            return (_jsx(PatientProjectionDetailStep, { decrypted: item.decrypted, content: item.content, taxonomy: flow.taxonomy, locale: locale, onBack: () => setState({ kind: 'loaded', records: state.records }) }));
        }
    }
}
//# sourceMappingURL=patient-projection-view.js.map